"use client"

import { useState } from "react"
import {
  Lock,
  CheckCircle,
  Loader2,
  ArrowRight,
  AlertTriangle,
  Calendar,
  Scissors,
  Droplets,
  Package,
  Bug,
  Leaf,
} from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { SocialLoginButtons } from "@/components/auth/SocialLoginButtons"
import { detectEmailTypo, type EmailSuggestion } from "@/lib/email-typo-detection"
import { analytics } from "@/lib/analytics"
import { useImpressionEvent } from "@/hooks/useImpressionEvent"

interface GuideEmailGateProps {
  grassType: string
  grassName: string
  onUnlock: () => void
  onBeforeOAuthRedirect?: () => void
}

const INCLUDED_ITEMS = [
  { icon: Package, label: "Complete fertilizer program" },
  { icon: Calendar, label: "Month-by-month care calendar" },
  { icon: Scissors, label: "Mowing height & frequency guide" },
  { icon: Droplets, label: "Watering schedule & techniques" },
  { icon: Bug, label: "Pest & disease management" },
  { icon: Leaf, label: "Seeding, aeration & more" },
]

export function GuideEmailGate({
  grassType,
  grassName,
  onUnlock,
  onBeforeOAuthRedirect,
}: GuideEmailGateProps) {
  const [email, setEmail] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState("")
  const [emailSuggestion, setEmailSuggestion] = useState<EmailSuggestion | null>(null)

  // source "guide" matches the signup source on line ~93 (the "guide_returning"
  // dup variant folds into the same surface_family) so the funnel joins.
  const impressionRef = useImpressionEvent<HTMLDivElement>("guide", { grassType })

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setEmail(value)
    setError("")

    if (value.includes("@") && value.includes(".")) {
      const suggestion = detectEmailTypo(value)
      setEmailSuggestion(suggestion)
    } else {
      setEmailSuggestion(null)
    }
  }

  const handleAcceptSuggestion = () => {
    if (emailSuggestion) {
      setEmail(emailSuggestion.suggested)
      setEmailSuggestion(null)
    }
  }

  const handleSubmit = async () => {
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("Please enter a valid email address")
      return
    }

    setIsSubmitting(true)
    setError("")

    try {
      const response = await fetch("/api/email/subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          context: "guide",
          grassType,
        }),
      })

      const data = await response.json()

      if (response.ok || data.error === "already_subscribed") {
        const source = data.error === "already_subscribed" ? "guide_returning" : "guide"
        analytics.newsletterSignup(email, source)
        onUnlock()
      } else {
        setError(data.error || "Something went wrong. Please try again.")
      }
    } catch {
      setError("Network error. Please try again.")
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSubmit()
    }
  }

  return (
    <div ref={impressionRef} className="relative">
      {/* Decorative top border */}
      <div className="h-1 bg-linear-to-r from-green-400 via-emerald-500 to-green-400 rounded-t-2xl" />

      <div className="bg-white border border-gray-200 border-t-0 rounded-b-2xl shadow-xl p-6 md:p-8">
        <div className="max-w-lg mx-auto text-center">
          {/* Lock icon */}
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-linear-to-br from-green-100 to-emerald-100 mb-4">
            <Lock className="w-7 h-7 text-green-600" />
          </div>

          {/* Heading */}
          <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">
            Get the complete {grassName} playbook
          </h3>
          <p className="text-gray-500 text-sm mb-6">
            It is free to read. Every section unlocks instantly, no spam.
          </p>

          {/* What's included grid */}
          <div className="grid grid-cols-2 gap-2 mb-6">
            {INCLUDED_ITEMS.map((item) => (
              <div
                key={item.label}
                className="flex items-center gap-2 text-left text-sm text-gray-600"
              >
                <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                <span>{item.label}</span>
              </div>
            ))}
          </div>

          {/* Social Login */}
          <SocialLoginButtons
            redirectTo={typeof window !== "undefined" ? window.location.href : undefined}
            onBeforeRedirect={onBeforeOAuthRedirect}
          />

          {/* Divider */}
          <div className="flex items-center gap-3 my-4">
            <div className="flex-1 h-px bg-gray-200" />
            <span className="text-xs text-gray-400">or continue with email</span>
            <div className="flex-1 h-px bg-gray-200" />
          </div>

          {/* Email-path sub-promise — only the email submit triggers the
              care-plan send, so promise it here (not above OAuth). */}
          <p className="text-xs text-gray-500 mb-3">
            The full guide opens now, and your personalized 12-month {grassName} care calendar lands in your inbox.
          </p>

          {/* Email input */}
          <div className="space-y-3">
            <Input
              type="email"
              name="email"
              inputMode="email"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              value={email}
              onChange={handleEmailChange}
              onKeyDown={handleKeyPress}
              placeholder="Enter your email address"
              className="w-full border-2 border-green-200 rounded-xl px-4 py-4 text-lg focus:border-green-500 focus:ring-0 focus:outline-hidden transition-colors"
              autoComplete="email"
              style={{ boxShadow: "none" }}
            />

            {/* Typo suggestion */}
            {emailSuggestion && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0" />
                  <span className="text-sm text-amber-800">
                    Did you mean{" "}
                    <strong>{emailSuggestion.correctedDomain}</strong>?
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleAcceptSuggestion}
                    className="text-sm font-medium text-green-700 hover:text-green-800 px-2 py-1 rounded hover:bg-green-50 transition-colors"
                  >
                    Yes, fix it
                  </button>
                  <button
                    type="button"
                    onClick={() => setEmailSuggestion(null)}
                    className="text-sm text-gray-500 hover:text-gray-700 px-2 py-1 rounded hover:bg-gray-100 transition-colors"
                  >
                    No
                  </button>
                </div>
              </div>
            )}

            {/* Error */}
            {error && (
              <p className="text-sm text-red-600 text-center">{error}</p>
            )}

            {/* Submit */}
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="group w-full bg-linear-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white py-4 px-6 rounded-xl font-semibold text-lg shadow-md hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.99] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-md min-h-[56px]"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 mr-3 animate-spin" />
                  Sending your plan...
                </>
              ) : (
                <>
                  Email Me My Care Calendar
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-0.5 transition-transform duration-200" />
                </>
              )}
            </Button>

            {/* Trust indicators */}
            <p className="text-xs text-gray-400 text-center">
              Free &bull; No spam &bull; Instant access
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
